import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { parseScheduleConflictError, ScheduleConflictDetails } from '../utils/scheduleConflictMessage';
import { getAbsenceLabel, isAutoNoShowAbsence } from '../utils/absence';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tooltip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Autocomplete,
  Checkbox,
  LinearProgress,
  TableSortLabel,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  ViewWeek as WeekIcon,
  ViewModule as MonthIcon,
  Add as AddIcon,
  Publish as PublishIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { apiClient } from '../services/api';
import { Campaign, User } from '../types';
import { useAuth } from '../contexts/AuthContext';
import {
  TimeDisplayMode,
  describeShiftTimes,
  resolveViewerTimezone,
  zoneLabel,
  isValidZone,
} from '../utils/scheduleTimeDisplay';
import { CampaignFilterService } from '../services/campaignFilterService';
import {
  getApprovedAbsenceDisplayLabel,
  isApprovedVacationAbsence,
  isManualDayOffAbsence,
  isTimeOffSyncReason,
  resolveScheduleAbsenceReason,
} from '../constants/absenceReasons';

const absenceDisplayPriority = (absence: { absenceType?: string; reason?: string | null }) => {
  if (isManualDayOffAbsence(absence)) return 100;
  if (isTimeOffSyncReason(absence.reason)) return 105;
  if (isApprovedVacationAbsence(absence)) return 90;
  if (absence.absenceType === 'holiday') return 80;
  if (absence.absenceType === 'personal') return 70;
  if (absence.absenceType === 'sick') return 60;
  if (absence.absenceType === 'no_show' || absence.reason === 'NO_CALL_NO_SHOW') return 55;
  if (absence.absenceType === 'other') return 50;
  return 10;
};

function resolveAbsenceCellStatus(absence: { absenceType?: string; reason?: string | null }) {
  if (isManualDayOffAbsence(absence)) {
    return { key: 'day_off', label: STATUS_META.day_off.label };
  }
  if (absence.absenceType === 'holiday') {
    return { key: 'holiday', label: STATUS_META.holiday.label };
  }
  if (isApprovedVacationAbsence(absence)) {
    return { key: 'vacation', label: STATUS_META.vacation.label };
  }
  if (absence.absenceType === 'personal' || absence.reason === 'PERSONAL') {
    return { key: 'personal', label: STATUS_META.personal.label };
  }
  if (absence.absenceType === 'no_show' || absence.reason === 'NO_CALL_NO_SHOW') {
    return { key: 'no_show', label: STATUS_META.no_show.label };
  }
  if (absence.absenceType === 'sick' || absence.reason === 'SICK') {
    return { key: 'sick', label: STATUS_META.sick.label };
  }
  if (absence.absenceType === 'suspension' || absence.reason === 'SUSPENSION') {
    return { key: 'suspension', label: STATUS_META.suspension.label };
  }
  if (absence.absenceType === 'training' || absence.reason === 'TRAINING') {
    return { key: 'training', label: STATUS_META.training.label };
  }
  if (absence.absenceType === 'other' && !isManualDayOffAbsence(absence)) {
    return { key: 'other', label: STATUS_META.other.label };
  }
  const label = getApprovedAbsenceDisplayLabel(absence);
  const key = absence.absenceType || 'other';
  return { key, label };
};

const STATUS_OPTIONS = ['all', 'scheduled', 'missing', 'day_off'] as const;

const STATUS_META: Record<string, { label: string; color: string; textColor?: string }> = {
  scheduled: { label: 'Scheduled', color: '#1e88e5' },
  day_off: { label: 'Day Off', color: '#c8e6c9', textColor: '#2e7d32' },
  vacation: { label: 'Vacation', color: '#e1bee7', textColor: '#6a1b9a' },
  personal: { label: 'Personal Day', color: '#ffcc80', textColor: '#e65100' },
  holiday: { label: 'Holiday', color: '#7e57c2' },
  no_show: { label: 'No Call / No show', color: '#e53935', textColor: '#fff' },
  sick: { label: 'Sick', color: '#ef9a9a', textColor: '#b71c1c' },
  suspension: { label: 'Suspension', color: '#90a4ae', textColor: '#263238' },
  training: { label: 'Training', color: '#b2dfdb', textColor: '#00695c' },
  other: { label: 'Other', color: '#cfd8dc', textColor: '#455a64' },
  missing: { label: 'Missing', color: '#e53935' },
  not_scheduled: { label: 'Not Scheduled', color: '#ffffff' },
  present: { label: 'Present', color: '#43a047' },
  unscheduled_work: { label: 'Unscheduled Work', color: '#f9a825' },
  /** Scheduled on a different campaign than the filtered/viewed campaign. */
  other_campaign: { label: 'Other Campaign', color: '#0288d1' },
  other_campaign_today: { label: 'Other Campaign (Today)', color: '#2e7d32' },
};

const getOtherCampaignStatusMeta = (date: string) => {
  if (isTodayDate(date)) {
    return STATUS_META.other_campaign_today;
  }
  if (isPastDate(date)) {
    return { label: STATUS_META.other_campaign.label, color: '#90caf9', textColor: '#0d47a2' };
  }
  // Future: blue
  return STATUS_META.other_campaign;
};

const ABSENCE_TYPES = [
  { value: 'vacation', label: 'Vacation' },
  { value: 'sick', label: 'Sick' },
  { value: 'personal', label: 'Personal' },
  { value: 'other', label: 'Other' },
  { value: 'unexcused', label: 'Unexcused' },
  { value: 'holiday', label: 'Holiday' },
  { value: 'bereavement', label: 'Bereavement' },
  { value: 'maternity', label: 'Maternity' },
  { value: 'jury_duty', label: 'Jury Duty' },
  { value: 'training', label: 'Training' },
  { value: 'suspension', label: 'Suspension' },
];

const formatShiftTime = (time?: string | null) => {
  if (!time) return '';
  return time.length > 5 ? time.slice(0, 5) : time;
};

const formatShiftRange = (schedule: { startTime?: string | null; endTime?: string | null }) => {
  const start = formatShiftTime(schedule.startTime);
  const end = formatShiftTime(schedule.endTime);
  return start && end ? `${start}–${end}` : start || end || '—';
};

const getShiftDurationHours = (schedule: { startTime?: string | null; endTime?: string | null }) => {
  const start = formatShiftTime(schedule.startTime);
  const end = formatShiftTime(schedule.endTime);
  if (!start || !end) return 0;
  const [startHour, startMinute] = start.split(':').map(Number);
  const [endHour, endMinute] = end.split(':').map(Number);
  let minutes = (endHour * 60 + endMinute) - (startHour * 60 + startMinute);
  if (minutes < 0) minutes += 24 * 60;
  return Math.round((minutes / 60) * 100) / 100;
};

const roundHours = (value: number) => Math.round(value * 100) / 100;

const getEmployeeWeekScheduledHours = (
  emp: GridEmployee,
  dates: string[],
  getShifts: (userId: string, date: string, campaignId: string) => ShiftSchedule[],
) => (
  dates.reduce((total, date) => {
    const shifts = getShifts(emp.id, date, emp.gridCampaignId);
    return total + shifts.reduce((sum, shift) => sum + getShiftDurationHours(shift), 0);
  }, 0)
);

const getWeekStartDate = (date: Date) => {
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  const start = new Date(date);
  start.setDate(diff);
  return start;
};

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const getWeekDates = (startDate: string) => {
  const start = new Date(startDate + 'T00:00:00');
  const dates: string[] = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    dates.push(`${y}-${m}-${d}`);
  }
  return dates;
};

const getMonthDates = (monthStr: string) => {
  const [year, month] = monthStr.split('-').map(Number);
  const firstDay = new Date(year, month - 1, 1);
  const start = getWeekStartDate(firstDay);
  const dates: string[] = [];
  for (let i = 0; i < 42; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    dates.push(`${y}-${m}-${d}`);
  }
  return dates;
};

const getLocalDateString = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const isPastDate = (dateStr: string) => {
  const today = getLocalDateString(new Date());
  return dateStr < today;
};

const isTodayDate = (dateStr: string) => dateStr === getLocalDateString(new Date());

const isTodayOrFutureDate = (dateStr: string) => !isPastDate(dateStr);

const addDaysToDateString = (dateStr: string, days: number) => {
  const date = new Date(dateStr + 'T00:00:00');
  date.setDate(date.getDate() + days);
  return getLocalDateString(date);
};

const getCopyEmployeeKey = (emp: GridEmployee) => `${emp.id}_${emp.gridCampaignId}`;

function mergeEmployeesWithSchedules(
  baseEmployees: GridEmployee[],
  schedules: any[],
  campaignId: string,
  campaignName: string,
): GridEmployee[] {
  const map = new Map<string, GridEmployee>();
  baseEmployees.forEach((emp) => map.set(emp.id, emp));

  schedules.forEach((schedule) => {
    if (!schedule?.userId || map.has(schedule.userId)) return;
    const user = schedule.user;
    if (!user?.id) return;
    map.set(user.id, {
      ...user,
      gridCampaignId: campaignId,
      gridCampaignName: campaignName,
    });
  });

  return Array.from(map.values());
}

interface GridEmployee extends User {
  gridCampaignId: string;
  gridCampaignName: string;
}

interface ShiftSchedule {
  id?: string;
  userId?: string;
  date?: string;
  campaignId?: string;
  startTime?: string;
  endTime?: string;
  // UTC instant + employee zone power the "My Time" / "Agent Time" display toggle.
  startTimeUTC?: string;
  endTimeUTC?: string;
  userTimezone?: string;
  user?: { timezone?: string | null; workLocation?: string | null };
  campaign?: { id?: string; name?: string };
}

interface AssignFormData {
  userId: string;
  campaignId: string;
  date: string;
  scheduleId: string | null;
  startTime: string;
  endTime: string;
  repeat: 'none' | 'daily' | 'weekly' | 'biweekly' | 'monthly';
  repeatEndDate: string;
  status: 'scheduled' | 'day_off' | 'vacation' | 'holiday' | 'not_scheduled';
  reason: string;
}

interface DayShiftsDialogState {
  open: boolean;
  userId: string;
  date: string;
  employeeName: string;
  campaignId: string;
  /** When true, dialog lists shifts on other campaigns (read-only details). */
  otherCampaignOnly: boolean;
}

interface AddEmployeeFormData {
  selectedEmployees: User[];
  permanent: boolean;
}

const getEmployeeOptionLabel = (employee: User) =>
  `${employee.firstName} ${employee.lastName} (${employee.employeeId})`;

interface CampaignListboxProps extends React.HTMLAttributes<HTMLUListElement> {
  onToggleAll?: () => void;
  allSelected?: boolean;
  someSelected?: boolean;
}

const CampaignListbox = React.forwardRef<HTMLUListElement, CampaignListboxProps>(
  function CampaignListbox(
    { children, onToggleAll, allSelected = false, someSelected = false, ...other },
    ref,
  ) {
    return (
      <ul ref={ref} {...other}>
        <Box
          component="li"
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1,
            py: 0.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            position: 'sticky',
            top: 0,
            bgcolor: 'background.paper',
            zIndex: 1,
            listStyle: 'none',
          }}
          onMouseDown={(event) => event.preventDefault()}
        >
          <Checkbox
            size="small"
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            onChange={() => onToggleAll?.()}
          />
          <Typography variant="body2">
            {allSelected ? 'Exclude all' : 'All'}
          </Typography>
        </Box>
        {children}
      </ul>
    );
  },
);

const createCampaignListbox = (
  onToggleAll: () => void,
  allSelected: boolean,
  someSelected: boolean,
) =>
  React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLUListElement>>(
    function CampaignListboxWithActions(props, ref) {
      return (
        <CampaignListbox
          ref={ref}
          {...props}
          onToggleAll={onToggleAll}
          allSelected={allSelected}
          someSelected={someSelected}
        />
      );
    },
  );

type CopySortField = 'campaign' | 'employee' | 'employeeId' | string;
type GridSortField = 'campaign' | 'employee' | 'employeeId' | 'status';

const ScheduleManagementPage: React.FC = () => {
  const { user } = useAuth();
  // Schedule-grid time-zone DISPLAY mode. Stored times are never changed.
  // 'mine' = logged-in viewer's zone (default); 'agent' = employee's own zone.
  const [timeMode, setTimeMode] = useState<TimeDisplayMode>(() => {
    try {
      const v = localStorage.getItem('scheduleTimeMode');
      if (v === 'mine' || v === 'agent') return v;
    } catch { /* ignore */ }
    return 'mine';
  });
  const viewerTimezone = useMemo(() => resolveViewerTimezone(user), [user]);
  const displayShiftRange = useCallback(
    (shift: ShiftSchedule) => describeShiftTimes(shift, timeMode, viewerTimezone).text,
    [timeMode, viewerTimezone],
  );
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([]);
  const [campaignInfo, setCampaignInfo] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'weekly' | 'monthly'>('weekly');
  const [selectedWeek, setSelectedWeek] = useState<string>(() => {
    const today = new Date();
    const weekStart = getWeekStartDate(today);
    const y = weekStart.getFullYear();
    const m = String(weekStart.getMonth() + 1).padStart(2, '0');
    const d = String(weekStart.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  });
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  });

  const [statusFilter, setStatusFilter] = useState<typeof STATUS_OPTIONS[number]>('scheduled');
  const [searchQuery, setSearchQuery] = useState('');
  const [gridSortField, setGridSortField] = useState<GridSortField>('campaign');
  const [gridSortDirection, setGridSortDirection] = useState<'asc' | 'desc'>('asc');

  const [employees, setEmployees] = useState<GridEmployee[]>([]);
  const [temporaryEmployees, setTemporaryEmployees] = useState<User[]>([]);
  const [schedules, setSchedules] = useState<any[]>([]);
  const [employeeShifts, setEmployeeShifts] = useState<ShiftSchedule[]>([]);
  const [absences, setAbsences] = useState<any[]>([]);
  const [timeEntries, setTimeEntries] = useState<any[]>([]);
  const [publishStatus, setPublishStatus] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scheduleConflict, setScheduleConflict] = useState<ScheduleConflictDetails | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignFormData, setAssignFormData] = useState<AssignFormData>({
    userId: '',
    campaignId: '',
    date: '',
    scheduleId: null,
    startTime: '09:00',
    endTime: '17:00',
    repeat: 'none',
    repeatEndDate: '',
    status: 'scheduled',
    reason: '',
  });

  const [dayShiftsDialog, setDayShiftsDialog] = useState<DayShiftsDialogState>({
    open: false,
    userId: '',
    date: '',
    employeeName: '',
    campaignId: '',
    otherCampaignOnly: false,
  });

  // Shift times in the editor are always entered/stored in the EMPLOYEE's local
  // wall-clock time (never the viewer's), so we label the fields with the agent's
  // own zone. Display-only "My Time" conversion never feeds back into these inputs.
  const editorTimeHelper = useMemo(() => {
    const emp = employees.find((e) => e.id === assignFormData.userId);
    const tz = emp?.timezone && isValidZone(emp.timezone) ? emp.timezone : null;
    const who = emp ? `${emp.firstName} ${emp.lastName}`.trim() : 'Employee';
    return tz
      ? `${who}'s local time (${zoneLabel(tz)})`
      : 'Entered in the employee’s local time';
  }, [employees, assignFormData.userId]);

  const [addEmployeeDialogOpen, setAddEmployeeDialogOpen] = useState(false);
  const [addEmployeeFormData, setAddEmployeeFormData] = useState<AddEmployeeFormData>({
    selectedEmployees: [],
    permanent: false,
  });
  const [allEmployees, setAllEmployees] = useState<User[]>([]);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyEndDate, setCopyEndDate] = useState('');
  const [copySelectedEmployeeKeys, setCopySelectedEmployeeKeys] = useState<Set<string>>(new Set());
  const [copySortField, setCopySortField] = useState<CopySortField>('campaign');
  const [copySortDirection, setCopySortDirection] = useState<'asc' | 'desc'>('asc');
  const [copying, setCopying] = useState(false);
  const activeGridLoadRef = useRef(0);

  const canEdit = Boolean(
    user && (
      ['team_lead', 'supervisor', 'manager', 'administrator', 'super_user', 'hr'].includes(user.role)
      || user.permissions?.includes('schedules.update')
    ),
  );
  const canAssignVacation = Boolean(
    user && ['administrator', 'super_user'].includes(user.role),
  );
  const isSchedulePublished = Boolean(publishStatus?.publishedAt);
  const canEditSchedule = canEdit && !isSchedulePublished;
  const canEditCell = useCallback(
    (date: string) => canEdit && isTodayOrFutureDate(date),
    [canEdit],
  );

  const handleScheduleApiError = (err: unknown, fallback: string) => {
    const axiosErr = err as { response?: { data?: { code?: string; message?: string } } };
    if (axiosErr?.response?.data?.code === 'ABSENCE_EXISTS') {
      setError(
        axiosErr.response.data.message
        || 'Employee has an absence on this date. Confirm to remove it when saving the schedule.',
      );
      setScheduleConflict(null);
      return;
    }
    const parsed = parseScheduleConflictError(err);
    setError(parsed.message || fallback);
    setScheduleConflict(parsed.conflict);
  };

  const goToConflictingSchedule = () => {
    if (!scheduleConflict) return;
    setSelectedCampaignIds([scheduleConflict.campaignId]);
    const conflictDate = scheduleConflict.date;
    if (viewMode === 'weekly') {
      const d = new Date(`${conflictDate}T12:00:00`);
      const day = d.getDay();
      const offset = day === 0 ? -6 : 1 - day;
      d.setDate(d.getDate() + offset);
      setSelectedWeek(d.toISOString().slice(0, 10));
    } else {
      setSelectedMonth(conflictDate.slice(0, 7));
    }
    setScheduleConflict(null);
    setError(null);
    setAssignDialogOpen(false);
  };

  const dateRange = useMemo(() => {
    if (viewMode === 'weekly') {
      const dates = getWeekDates(selectedWeek);
      return { dates, startDate: dates[0], endDate: dates[dates.length - 1] };
    }
    const dates = getMonthDates(selectedMonth);
    return { dates, startDate: dates[0], endDate: dates[dates.length - 1] };
  }, [viewMode, selectedWeek, selectedMonth]);

  const selectedCampaigns = useMemo(
    () => campaigns.filter((c) => selectedCampaignIds.includes(c.id)),
    [campaigns, selectedCampaignIds],
  );
  const singleCampaignId = selectedCampaignIds.length === 1 ? selectedCampaignIds[0] : '';
  const isMultiCampaignView = selectedCampaignIds.length > 1;
  const allCampaignsSelected = campaigns.length > 0 && selectedCampaignIds.length === campaigns.length;
  const someCampaignsSelected = selectedCampaignIds.length > 0 && !allCampaignsSelected;

  const handleToggleAllCampaigns = useCallback(() => {
    if (selectedCampaignIds.length === campaigns.length) {
      // Exclude all
      setSelectedCampaignIds([]);
      return;
    }
    setSelectedCampaignIds(campaigns.map((campaign) => campaign.id));
  }, [campaigns, selectedCampaignIds.length]);

  const CampaignListboxWithActions = useMemo(
    () => createCampaignListbox(handleToggleAllCampaigns, allCampaignsSelected, someCampaignsSelected),
    [handleToggleAllCampaigns, allCampaignsSelected, someCampaignsSelected],
  );

  const loadCampaigns = useCallback(async () => {
    try {
      const response = await apiClient.campaigns.getAll();
      const data = response.data.data || [];
      const accessible = user ? CampaignFilterService.getUserAccessibleCampaigns(user, data) : data;
      setCampaigns(accessible);
      if (selectedCampaignIds.length === 0 && accessible.length > 0) {
        setSelectedCampaignIds([accessible[0].id]);
      }
    } catch (err) {
      console.error('Error loading campaigns:', err);
      setError('Failed to load campaigns');
    }
  }, [user, selectedCampaignIds.length]);

  const loadAllEmployees = useCallback(async () => {
    try {
      const response = await apiClient.users.getAll();
      setAllEmployees(response.data.data || []);
    } catch (err) {
      console.error('Error loading employees:', err);
    }
  }, []);

  const scheduleKey = useCallback((userId: string, date: string) => `${userId}_${date}`, []);

  const loadGridData = useCallback(async () => {
    if (selectedCampaignIds.length === 0) return;

    const dates = viewMode === 'weekly' ? getWeekDates(selectedWeek) : getMonthDates(selectedMonth);
    const startDate = dates[0];
    const endDate = dates[dates.length - 1];
    const loadToken = ++activeGridLoadRef.current;

    const isStale = () => loadToken !== activeGridLoadRef.current;

    try {
      setLoading(true);
      setError(null);

      const campaignResults = await Promise.all(
        selectedCampaignIds.map(async (campaignId) => {
          const [employeesResponse, scheduleResponse] = await Promise.all([
            apiClient.schedules.getCampaignEmployees(campaignId, startDate, endDate),
            apiClient.schedules.getCampaignSchedules(campaignId, startDate, endDate),
          ]);
          return { campaignId, employeesResponse, scheduleResponse };
        }),
      );
      if (isStale()) return;

      const allEmployees: GridEmployee[] = [];
      const allSchedules: any[] = [];
      let primaryCampaignData: any = null;

      campaignResults.forEach(({ campaignId, employeesResponse, scheduleResponse }) => {
        const campaignData = employeesResponse.data.data;
        const campaignName = campaignData?.campaign?.name
          || campaigns.find((c) => c.id === campaignId)?.name
          || '';
        if (selectedCampaignIds.length === 1) {
          primaryCampaignData = campaignData?.campaign || null;
        }
        const scheduleRows = scheduleResponse.data.data || [];
        allSchedules.push(...scheduleRows);
        const baseEmployees = (campaignData?.departmentEmployees || []).map((emp: User) => ({
          ...emp,
          gridCampaignId: campaignId,
          gridCampaignName: campaignName,
        }));
        allEmployees.push(...mergeEmployeesWithSchedules(
          baseEmployees,
          scheduleRows,
          campaignId,
          campaignName,
        ));
      });

      setCampaignInfo(primaryCampaignData);
      setSchedules(allSchedules);
      setEmployees(allEmployees);

      const userIds = Array.from(new Set([
        ...allEmployees.map((emp) => emp.id),
        ...allSchedules.map((row: any) => row.userId),
      ]));
      if (userIds.length > 0) {
        const shiftsResponse = await apiClient.schedules.getEmployeeShifts(startDate, endDate, userIds);
        if (isStale()) return;
        setEmployeeShifts((shiftsResponse.data.data || []) as ShiftSchedule[]);
      } else {
        setEmployeeShifts([]);
      }

      if (selectedCampaignIds.length === 1) {
        const publishResponse = await apiClient.schedules.getPublishStatus(
          selectedCampaignIds[0],
          startDate,
          endDate,
          viewMode,
        );
        if (isStale()) return;
        setPublishStatus(publishResponse.data.data || null);
      } else {
        setPublishStatus(null);
      }

      // Match attendance coverage: approved absences + pending auto no-shows.
      // Auto NCNS rows are created as status=pending; approved-only hid them as "Missing".
      const baseAbsenceParams = new URLSearchParams();
      baseAbsenceParams.append('startDate', startDate);
      baseAbsenceParams.append('endDate', endDate);
      baseAbsenceParams.append('page', '1');
      baseAbsenceParams.append('limit', '5000');
      const approvedParams = new URLSearchParams(baseAbsenceParams);
      approvedParams.append('status', 'approved');
      const pendingNoShowParams = new URLSearchParams(baseAbsenceParams);
      pendingNoShowParams.append('status', 'pending');
      pendingNoShowParams.append('absenceType', 'no_show');
      const [approvedResponse, pendingNoShowResponse] = await Promise.all([
        apiClient.absences.getAll(approvedParams.toString()),
        apiClient.absences.getAll(pendingNoShowParams.toString()),
      ]);
      const absenceById = new Map<string, any>();
      for (const row of [
        ...(approvedResponse.data.data || []),
        ...(pendingNoShowResponse.data.data || []),
      ]) {
        if (row?.id) absenceById.set(row.id, row);
      }
      const absenceRows = Array.from(absenceById.values());

      const timeEntriesResponse = await apiClient.timeEntries.getReport({
        startDate,
        endDate,
        ...(selectedCampaignIds.length === 1 ? { campaignId: selectedCampaignIds[0] } : {}),
        limit: 10000,
        page: 1,
      });

      if (isStale()) return;

      setAbsences(absenceRows);
      setTimeEntries(timeEntriesResponse.data.data || []);
    } catch (err: any) {
      if (!isStale()) {
        console.error('Error loading schedule data:', err);
        setError(err?.response?.data?.message || 'Failed to load schedule data');
      }
    } finally {
      if (!isStale()) {
        setLoading(false);
      }
    }
  }, [selectedCampaignIds, selectedWeek, selectedMonth, viewMode, campaigns]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => {
    if (addEmployeeDialogOpen) {
      loadAllEmployees();
    }
  }, [addEmployeeDialogOpen, loadAllEmployees]);

  useEffect(() => {
    if (selectedCampaignIds.length > 0) {
      loadGridData();
    }
  }, [selectedCampaignIds, selectedWeek, selectedMonth, viewMode, loadGridData]);

  useEffect(() => {
    setTemporaryEmployees([]);
  }, [selectedCampaignIds]);

  const getScheduleCampaignName = useCallback((schedule: any) => {
    if (schedule?.campaign?.name) return schedule.campaign.name;
    const match = campaigns.find((c) => c.id === schedule?.campaignId);
    if (match?.name) return match.name;
    return 'Unknown campaign';
  }, [campaigns]);

  const shouldShowShiftCampaign = useCallback((
    schedule: ShiftSchedule,
    shiftCount: number,
    viewCampaignId: string,
  ) => {
    const isOtherCampaign = Boolean(
      schedule.campaignId && schedule.campaignId !== viewCampaignId,
    );
    return isOtherCampaign || shiftCount > 1;
  }, []);

  const formatShiftLabel = useCallback((
    schedule: ShiftSchedule,
    options?: { shiftCount?: number; viewCampaignId?: string },
  ) => {
    const shiftCount = options?.shiftCount ?? 1;
    const viewCampaignId = options?.viewCampaignId ?? schedule.campaignId ?? '';
    const timeRange = displayShiftRange(schedule);
    if (shouldShowShiftCampaign(schedule, shiftCount, viewCampaignId)) {
      return `${getScheduleCampaignName(schedule)} · ${timeRange}`;
    }
    return timeRange;
  }, [getScheduleCampaignName, shouldShowShiftCampaign, displayShiftRange]);

  const schedulesByCell = useMemo(() => {
    const map = new Map<string, ShiftSchedule[]>();
    employeeShifts.forEach((schedule) => {
      const userId = schedule.userId;
      const date = schedule.date;
      if (!userId || !date) return;
      const key = scheduleKey(userId, date);
      const list = map.get(key) || [];
      list.push(schedule);
      map.set(key, list);
    });
    map.forEach((list, key) => {
      list.sort((a, b) => String(a.startTime || '').localeCompare(String(b.startTime || '')));
      map.set(key, list);
    });
    return map;
  }, [employeeShifts, scheduleKey]);

  const getCellSchedules = useCallback(
    (userId: string, date: string): ShiftSchedule[] =>
      schedulesByCell.get(scheduleKey(userId, date)) || [],
    [schedulesByCell, scheduleKey],
  );

  const getEmployeeCellSchedules = useCallback(
    (emp: GridEmployee, date: string): ShiftSchedule[] => {
      const all = getCellSchedules(emp.id, date);
      return all.filter((schedule) => !schedule.campaignId || schedule.campaignId === emp.gridCampaignId);
    },
    [getCellSchedules],
  );

  const getCellSchedulesForCampaign = useCallback(
    (userId: string, date: string, campaignId: string): ShiftSchedule[] =>
      getCellSchedules(userId, date).filter(
        (schedule) => !schedule.campaignId || schedule.campaignId === campaignId,
      ),
    [getCellSchedules],
  );

  const absenceMap = useMemo(() => {
    const map = new Map<string, any>();
    absences.forEach(absence => {
      const start = absence.startDate;
      const end = absence.endDate;
      if (!start || !end) return;
      const current = new Date(start + 'T00:00:00');
      const endDate = new Date(end + 'T00:00:00');
      while (current <= endDate) {
        const y = current.getFullYear();
        const m = String(current.getMonth() + 1).padStart(2, '0');
        const d = String(current.getDate()).padStart(2, '0');
        const date = `${y}-${m}-${d}`;
        const key = `${absence.userId}_${date}`;
        const existing = map.get(key);
        if (!existing || absenceDisplayPriority(absence) > absenceDisplayPriority(existing)) {
          map.set(key, absence);
        }
        current.setDate(current.getDate() + 1);
      }
    });
    return map;
  }, [absences]);

  const timeEntryMap = useMemo(() => {
    const map = new Set<string>();
    timeEntries.forEach(entry => {
      const date = new Date(entry.timestamp).toISOString().split('T')[0];
      map.add(`${entry.userId}_${date}`);
    });
    return map;
  }, [timeEntries]);

  const mergedEmployees = useMemo(() => {
    const map = new Map<string, GridEmployee>();
    employees.forEach(emp => map.set(`${emp.id}_${emp.gridCampaignId}`, emp));
    temporaryEmployees.forEach(emp => {
      const gridEmp = emp as GridEmployee;
      const campaignId = gridEmp.gridCampaignId || singleCampaignId;
      map.set(`${emp.id}_${campaignId}`, {
        ...emp,
        gridCampaignId: campaignId,
        gridCampaignName: gridEmp.gridCampaignName || campaignInfo?.name || '',
      });
    });
    return Array.from(map.values());
  }, [employees, temporaryEmployees, singleCampaignId, campaignInfo?.name]);

  const getOtherCampaignSchedules = useCallback(
    (emp: GridEmployee, date: string): ShiftSchedule[] =>
      getCellSchedules(emp.id, date).filter(
        (schedule) => Boolean(schedule.campaignId) && schedule.campaignId !== emp.gridCampaignId,
      ),
    [getCellSchedules],
  );

  const getEmployeeCellStatus = (emp: GridEmployee, date: string) => {
    const absence = absenceMap.get(`${emp.id}_${date}`);
    const cellSchedules = getEmployeeCellSchedules(emp, date);
    const hasClock = timeEntryMap.has(`${emp.id}_${date}`);

    if (absence) {
      return resolveAbsenceCellStatus(absence);
    }

    if (cellSchedules.length > 0) {
      if (hasClock) {
        return { key: 'present', label: STATUS_META.present.label };
      }
      if (isPastDate(date)) {
        return { key: 'missing', label: STATUS_META.missing.label };
      }
      return { key: 'scheduled', label: STATUS_META.scheduled.label };
    }

    if (hasClock) {
      return { key: 'unscheduled_work', label: STATUS_META.unscheduled_work.label };
    }

    const otherCampaignSchedules = getOtherCampaignSchedules(emp, date);
    if (otherCampaignSchedules.length > 0) {
      const uniqueCampaigns = new Set(
        otherCampaignSchedules.map((s) => s.campaignId).filter(Boolean) as string[],
      );
      return {
        key: 'other_campaign',
        label: uniqueCampaigns.size > 1 ? 'Multiple Campaigns' : STATUS_META.other_campaign.label,
      };
    }

    return { key: 'not_scheduled', label: STATUS_META.not_scheduled.label };
  };

  const employeesWithCampaignSchedules = useMemo(() => {
    const seen = new Set<string>();
    const result: GridEmployee[] = [];
    const temporaryIds = new Set(temporaryEmployees.map((emp) => emp.id));

    const addEmployee = (emp: GridEmployee) => {
      const key = `${emp.id}_${emp.gridCampaignId}`;
      if (!selectedCampaignIds.includes(emp.gridCampaignId) || seen.has(key)) return;
      seen.add(key);
      result.push(emp);
    };

    mergedEmployees.forEach((emp) => {
      const hasSchedule = dateRange.dates.some(
        (date) => getCellSchedulesForCampaign(emp.id, date, emp.gridCampaignId).length > 0,
      );
      const hasAbsence = dateRange.dates.some(
        (date) => absenceMap.has(`${emp.id}_${date}`),
      );
      const isExplicitlyAdded = temporaryIds.has(emp.id);
      if (hasSchedule || hasAbsence || isExplicitlyAdded) {
        addEmployee(emp);
      }
    });

    // Always surface employees added via the dialog, even before shifts exist.
    temporaryEmployees.forEach((emp) => {
      const campaignId = (emp as GridEmployee).gridCampaignId || singleCampaignId;
      if (!campaignId) return;
      addEmployee({
        ...emp,
        gridCampaignId: campaignId,
        gridCampaignName: (emp as GridEmployee).gridCampaignName
          || campaignInfo?.name
          || campaigns.find((c) => c.id === campaignId)?.name
          || '',
      } as GridEmployee);
    });

    schedules.forEach((schedule) => {
      if (!schedule.userId || !schedule.campaignId || !selectedCampaignIds.includes(schedule.campaignId)) {
        return;
      }
      const scheduleDate = String(schedule.date).slice(0, 10);
      if (!dateRange.dates.includes(scheduleDate)) return;
      const key = `${schedule.userId}_${schedule.campaignId}`;
      if (seen.has(key)) return;
      const user = schedule.user;
      if (!user?.id) return;
      addEmployee({
        ...user,
        gridCampaignId: schedule.campaignId,
        gridCampaignName: schedule.campaign?.name
          || campaigns.find((c) => c.id === schedule.campaignId)?.name
          || '',
      });
    });

    if (isMultiCampaignView) {
      return [...result].sort((a, b) => {
        const campaignCompare = a.gridCampaignName.localeCompare(b.gridCampaignName);
        if (campaignCompare !== 0) return campaignCompare;
        return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
      });
    }

    return result;
  }, [
    mergedEmployees,
    dateRange.dates,
    selectedCampaignIds,
    getCellSchedulesForCampaign,
    schedules,
    campaigns,
    isMultiCampaignView,
    temporaryEmployees,
    singleCampaignId,
    campaignInfo?.name,
    absenceMap,
  ]);

  const scheduledEmployeeCount = useMemo(
    () => employeesWithCampaignSchedules.filter((emp) =>
      dateRange.dates.some(
        (date) => getCellSchedulesForCampaign(emp.id, date, emp.gridCampaignId).length > 0,
      ),
    ).length,
    [employeesWithCampaignSchedules, dateRange.dates, getCellSchedulesForCampaign],
  );

  const addableEmployees = useMemo(() => {
    const onGridIds = new Set(employeesWithCampaignSchedules.map((emp) => emp.id));
    return allEmployees
      .filter((emp) => emp.isActive !== false)
      .filter((emp) => !onGridIds.has(emp.id))
      .sort((a, b) => getEmployeeOptionLabel(a).localeCompare(getEmployeeOptionLabel(b)));
  }, [allEmployees, employeesWithCampaignSchedules]);

  const filteredEmployees = useMemo(() => {
    const explicitGridIds = new Set(temporaryEmployees.map((emp) => emp.id));
    let list = employeesWithCampaignSchedules;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      list = list.filter(emp => {
        const fullName = `${emp.firstName} ${emp.lastName}`.toLowerCase();
        return fullName.includes(query) || (emp.employeeId || '').toLowerCase().includes(query);
      });
    }
    if (statusFilter !== 'all') {
      list = list.filter(emp => {
        if (explicitGridIds.has(emp.id)) return true;
        return dateRange.dates.some(date => {
          if (statusFilter === 'scheduled') {
            return getEmployeeCellSchedules(emp, date).length > 0;
          }
          const status = getEmployeeCellStatus(emp, date).key;
          if (statusFilter === 'day_off') {
            return status === 'day_off' || status === 'holiday';
          }
          return status === statusFilter;
        });
      });
    }
    return list;
  }, [
    employeesWithCampaignSchedules,
    searchQuery,
    statusFilter,
    dateRange.dates,
    getEmployeeCellSchedules,
    temporaryEmployees,
    getEmployeeCellStatus,
  ]);

  const sourceWeekStart = viewMode === 'weekly' ? selectedWeek : dateRange.startDate;
  const sourceWeekEnd = dateRange.dates[dateRange.dates.length - 1];
  const firstCopyWeekStart = addDaysToDateString(sourceWeekStart, 7);

  const copySourceEmployees = useMemo(() => (
    filteredEmployees.filter((emp) =>
      dateRange.dates.some((date) =>
        getCellSchedulesForCampaign(emp.id, date, emp.gridCampaignId).length > 0,
      ),
    )
  ), [filteredEmployees, dateRange.dates, getCellSchedulesForCampaign]);

  const selectedCopyEmployees = useMemo(
    () => copySourceEmployees.filter((emp) => copySelectedEmployeeKeys.has(getCopyEmployeeKey(emp))),
    [copySourceEmployees, copySelectedEmployeeKeys],
  );

  const copyJobsByCampaign = useMemo(() => {
    const map = new Map<string, { campaignId: string; campaignName: string; employees: GridEmployee[] }>();
    selectedCopyEmployees.forEach((emp) => {
      const existing = map.get(emp.gridCampaignId);
      if (existing) {
        existing.employees.push(emp);
        return;
      }
      map.set(emp.gridCampaignId, {
        campaignId: emp.gridCampaignId,
        campaignName: emp.gridCampaignName,
        employees: [emp],
      });
    });
    return Array.from(map.values());
  }, [selectedCopyEmployees]);

  const copyCampaignLabel = selectedCampaignIds.length === 1
    ? (campaignInfo?.name || selectedCampaigns[0]?.name || 'selected campaign')
    : `${selectedCampaignIds.length} selected campaigns`;

  const minCopyEndDate = addDaysToDateString(sourceWeekEnd, 7);

  const copyWeekCount = useMemo(() => {
    if (!copyEndDate || copyEndDate < minCopyEndDate) return 0;
    let count = 0;
    let targetWeekStart = firstCopyWeekStart;
    while (targetWeekStart <= copyEndDate) {
      count += 1;
      targetWeekStart = addDaysToDateString(targetWeekStart, 7);
    }
    return count;
  }, [copyEndDate, firstCopyWeekStart, minCopyEndDate]);

  const openCopyDialog = () => {
    setCopyEndDate(minCopyEndDate);
    setCopySelectedEmployeeKeys(new Set(copySourceEmployees.map(getCopyEmployeeKey)));
    setCopySortField('campaign');
    setCopySortDirection('asc');
    setCopyDialogOpen(true);
  };

  const handleCopySort = (field: CopySortField) => {
    if (copySortField === field) {
      setCopySortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setCopySortField(field);
    setCopySortDirection('asc');
  };

  const sortedCopySourceEmployees = useMemo(() => {
    const list = [...copySourceEmployees];
    const direction = copySortDirection === 'asc' ? 1 : -1;

    list.sort((a, b) => {
      let compare = 0;
      switch (copySortField) {
        case 'campaign':
          compare = a.gridCampaignName.localeCompare(b.gridCampaignName);
          break;
        case 'employee':
          compare = `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
          break;
        case 'employeeId':
          compare = (a.employeeId || '').localeCompare(b.employeeId || '', undefined, { numeric: true });
          break;
        default:
          if (copySortField === 'scheduledHours') {
            compare = getEmployeeWeekScheduledHours(a, dateRange.dates, getCellSchedulesForCampaign)
              - getEmployeeWeekScheduledHours(b, dateRange.dates, getCellSchedulesForCampaign);
          } else if (copySortField.startsWith('day:')) {
            const date = copySortField.slice(4);
            const aShifts = getCellSchedulesForCampaign(a.id, date, a.gridCampaignId);
            const bShifts = getCellSchedulesForCampaign(b.id, date, b.gridCampaignId);
            compare = aShifts.length - bShifts.length;
            if (compare === 0 && aShifts.length > 0 && bShifts.length > 0) {
              compare = formatShiftRange(aShifts[0]).localeCompare(formatShiftRange(bShifts[0]));
            }
          }
          break;
      }

      if (compare !== 0) return compare * direction;

      const campaignCompare = a.gridCampaignName.localeCompare(b.gridCampaignName);
      if (campaignCompare !== 0) return campaignCompare;
      return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
    });

    return list;
  }, [
    copySourceEmployees,
    copySortField,
    copySortDirection,
    getCellSchedulesForCampaign,
    dateRange.dates,
  ]);

  const toggleCopyEmployee = (key: string) => {
    setCopySelectedEmployeeKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllCopyEmployees = (checked: boolean) => {
    setCopySelectedEmployeeKeys(
      checked ? new Set(copySourceEmployees.map(getCopyEmployeeKey)) : new Set(),
    );
  };

  const allCopyEmployeesSelected = copySourceEmployees.length > 0
    && copySourceEmployees.every((emp) => copySelectedEmployeeKeys.has(getCopyEmployeeKey(emp)));
  const someCopyEmployeesSelected = copySourceEmployees.some(
    (emp) => copySelectedEmployeeKeys.has(getCopyEmployeeKey(emp)),
  );

  const handleCopySchedule = async () => {
    if (selectedCampaignIds.length === 0) return;
    if (copyEndDate < minCopyEndDate) {
      setError(`End date must be on or after ${formatDate(minCopyEndDate)}.`);
      return;
    }
    if (copyJobsByCampaign.length === 0) {
      setError('Select at least one employee to copy.');
      return;
    }

    try {
      setCopying(true);
      setError(null);
      let totalCopied = 0;

      for (const { campaignId, employees } of copyJobsByCampaign) {
        const userIds = employees.map((emp) => emp.id);
        let targetWeekStart = firstCopyWeekStart;

        while (targetWeekStart <= copyEndDate) {
          const response = await apiClient.schedules.copySchedules({
            campaignId,
            fromDate: sourceWeekStart,
            toDate: targetWeekStart,
            copyType: 'week',
            userIds,
          });
          totalCopied += response.data.data?.copiedSchedules || 0;
          targetWeekStart = addDaysToDateString(targetWeekStart, 7);
        }
      }

      setSuccess(
        `Copied ${totalCopied} schedule entries across ${copyWeekCount} week${copyWeekCount === 1 ? '' : 's'} for ${copyJobsByCampaign.length} campaign${copyJobsByCampaign.length === 1 ? '' : 's'}.`,
      );
      setCopyDialogOpen(false);
      await loadGridData();
    } catch (err: any) {
      console.error('Error copying schedules:', err);
      setError(err?.response?.data?.message || 'Failed to copy schedules');
    } finally {
      setCopying(false);
    }
  };

  const resolveShiftCampaignId = (shift: ShiftSchedule | null, fallback: string) => (
    shift?.campaignId || fallback
  );

  const openAssignDialogForSchedule = (
    emp: GridEmployee,
    date: string,
    schedule: ShiftSchedule | null,
    options?: { isNewShift?: boolean },
  ) => {
    const cellSchedules = getEmployeeCellSchedules(emp, date);
    const existing: ShiftSchedule | null = schedule || cellSchedules[0] || null;
    const status = getEmployeeCellStatus(emp, date).key;
    const absence = absenceMap.get(`${emp.id}_${date}`);

    if (status === 'day_off' || status === 'holiday' || status === 'vacation' || status === 'personal') {
      setAssignFormData({
        userId: emp.id,
        campaignId: resolveShiftCampaignId(schedule, resolveShiftCampaignId(existing, emp.gridCampaignId)),
        date,
        scheduleId: existing?.id || null,
        startTime: formatShiftTime(existing?.startTime) || '09:00',
        endTime: formatShiftTime(existing?.endTime) || '17:00',
        repeat: 'none',
        repeatEndDate: '',
        status: status === 'holiday'
          ? 'holiday'
          : status === 'vacation'
            ? 'vacation'
            : 'day_off',
        reason: absence?.reason || (status === 'day_off' ? 'DAY_OFF' : status === 'vacation' ? 'PTO' : ''),
      });
      setAssignDialogOpen(true);
      return;
    }

    setAssignFormData({
      userId: emp.id,
      campaignId: resolveShiftCampaignId(schedule, resolveShiftCampaignId(existing, emp.gridCampaignId)),
      date,
      scheduleId: options?.isNewShift ? null : (existing?.id || null),
      startTime: options?.isNewShift ? '09:00' : (formatShiftTime(existing?.startTime) || '09:00'),
      endTime: options?.isNewShift ? '17:00' : (formatShiftTime(existing?.endTime) || '17:00'),
      repeat: 'none',
      repeatEndDate: '',
      status: 'scheduled',
      reason: '',
    });
    setAssignDialogOpen(true);
  };

  const openDayShiftsDialog = (
    emp: GridEmployee,
    date: string,
    options?: { otherCampaignOnly?: boolean },
  ) => {
    setDayShiftsDialog({
      open: true,
      userId: emp.id,
      date,
      employeeName: `${emp.firstName} ${emp.lastName}`,
      campaignId: emp.gridCampaignId,
      otherCampaignOnly: Boolean(options?.otherCampaignOnly),
    });
  };

  const openAssignDialog = (emp: GridEmployee, date: string) => {
    if (!canEdit) {
      setError('You do not have permission to edit schedules.');
      return;
    }
    if (!emp.gridCampaignId) {
      setError('Select a campaign before editing this schedule.');
      return;
    }
    if (!canEditCell(date)) {
      if (isPastDate(date)) {
        setError('Past dates cannot be edited.');
      }
      return;
    }

    const cellSchedules = getEmployeeCellSchedules(emp, date);
    if (cellSchedules.length > 1) {
      openDayShiftsDialog(emp, date);
      return;
    }

    openAssignDialogForSchedule(emp, date, cellSchedules[0] || null);
  };

  const openOtherCampaignDetails = (emp: GridEmployee, date: string) => {
    openDayShiftsDialog(emp, date, { otherCampaignOnly: true });
  };

  const closeDayShiftsDialog = () => {
    setDayShiftsDialog((prev) => ({ ...prev, open: false }));
  };

  const handleDeleteShift = async (scheduleId: string) => {
    if (!window.confirm('Delete this shift?')) return;
    try {
      setLoading(true);
      const countBefore = getCellSchedulesForCampaign(
        dayShiftsDialog.userId,
        dayShiftsDialog.date,
        dayShiftsDialog.campaignId,
      ).length;
      await apiClient.schedules.delete(scheduleId);
      setSuccess('Shift deleted');
      await loadGridData();
      if (countBefore <= 1) {
        closeDayShiftsDialog();
      }
    } catch (err: any) {
      handleScheduleApiError(err, 'Failed to delete shift');
    } finally {
      setLoading(false);
    }
  };

  const handleClearAllShiftsForDay = async () => {
    const shifts = getCellSchedulesForCampaign(
      dayShiftsDialog.userId,
      dayShiftsDialog.date,
      dayShiftsDialog.campaignId,
    );
    if (!shifts.length) return;
    if (!window.confirm(`Clear all ${shifts.length} shifts for ${formatDate(dayShiftsDialog.date)}?`)) return;
    try {
      setLoading(true);
      await Promise.all(shifts.filter((s) => s.id).map((s) => apiClient.schedules.delete(s.id!)));
      setSuccess('All shifts cleared');
      closeDayShiftsDialog();
      await loadGridData();
    } catch (err: any) {
      handleScheduleApiError(err, 'Failed to clear shifts');
    } finally {
      setLoading(false);
    }
  };

  const addDays = (dateStr: string, days: number) => {
    const date = new Date(dateStr + 'T00:00:00');
    date.setDate(date.getDate() + days);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const addMonths = (dateStr: string, months: number) => {
    const date = new Date(dateStr + 'T00:00:00');
    date.setMonth(date.getMonth() + months);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const buildRepeatDates = () => {
    const dates = [assignFormData.date];
    if (assignFormData.repeat === 'none') return dates;
    if (!assignFormData.repeatEndDate) return dates;

    let cursor = assignFormData.date;
    while (true) {
      if (assignFormData.repeat === 'daily') cursor = addDays(cursor, 1);
      if (assignFormData.repeat === 'weekly') cursor = addDays(cursor, 7);
      if (assignFormData.repeat === 'biweekly') cursor = addDays(cursor, 14);
      if (assignFormData.repeat === 'monthly') cursor = addMonths(cursor, 1);

      if (cursor > assignFormData.repeatEndDate) break;
      dates.push(cursor);
    }
    return dates;
  };

  const handleAssignShift = async () => {
    if (!assignFormData.campaignId) return;
    if (!canEditCell(assignFormData.date)) {
      setError('Past dates cannot be edited.');
      return;
    }
    if (assignFormData.status === 'not_scheduled' && !confirmClearSchedule()) {
      return;
    }
    const dates = getAssignDates();
    if (assignFormData.status === 'scheduled' && !confirmScheduleChangeWithAbsences(dates)) {
      return;
    }
    try {
      setLoading(true);
      const targetCampaignId = assignFormData.campaignId;
      await Promise.all(dates.map(async (date) => {
        const cellSchedules = getCellSchedulesForCampaign(assignFormData.userId, date, assignFormData.campaignId);
        const existingSchedule = assignFormData.scheduleId
          ? cellSchedules.find((s) => s.id === assignFormData.scheduleId) || null
          : cellSchedules[0] || null;
        const existingAbsence = absenceMap.get(`${assignFormData.userId}_${date}`);

        if (assignFormData.status === 'not_scheduled') {
          if (assignFormData.scheduleId) {
            await apiClient.schedules.delete(assignFormData.scheduleId);
          } else {
            await Promise.all(cellSchedules.filter((s) => s.id).map((s) => apiClient.schedules.delete(s.id!)));
          }
          if (existingAbsence) {
            await apiClient.absences.delete(existingAbsence.id);
          }
          return;
        }

        if (assignFormData.status === 'day_off' || assignFormData.status === 'holiday' || assignFormData.status === 'vacation') {
          const absenceType = assignFormData.status === 'holiday'
            ? 'holiday'
            : assignFormData.status === 'vacation'
              ? 'vacation'
              : 'other';
          const resolvedReason = resolveScheduleAbsenceReason(assignFormData.status, assignFormData.reason);
          await Promise.all(cellSchedules.filter((s) => s.id).map((s) => apiClient.schedules.delete(s.id!)));
          if (existingAbsence) {
            await apiClient.absences.update(existingAbsence.id, {
              absenceType,
              startDate: date,
              endDate: date,
              status: 'approved',
              reason: resolvedReason,
              campaignId: targetCampaignId,
            });
          } else {
            await apiClient.absences.create({
              userId: assignFormData.userId,
              absenceType,
              startDate: date,
              endDate: date,
              status: 'approved',
              reason: resolvedReason,
              campaignId: targetCampaignId,
            });
          }
          return;
        }

        if (assignFormData.scheduleId && existingSchedule) {
          await apiClient.schedules.update(assignFormData.scheduleId, {
            userId: assignFormData.userId,
            campaignId: targetCampaignId,
            date,
            startTime: assignFormData.startTime,
            endTime: assignFormData.endTime,
            clearAbsence: Boolean(existingAbsence && !isAutoNoShowAbsence(existingAbsence)),
          } as Parameters<typeof apiClient.schedules.update>[1]);
        } else {
          await apiClient.schedules.create({
            userId: assignFormData.userId,
            campaignId: targetCampaignId,
            date,
            startTime: assignFormData.startTime,
            endTime: assignFormData.endTime,
            clearAbsence: Boolean(existingAbsence && !isAutoNoShowAbsence(existingAbsence)),
          } as Parameters<typeof apiClient.schedules.create>[0]);
        }
      }));
      setAssignDialogOpen(false);
      setSuccess(assignFormData.status === 'not_scheduled' ? 'Schedule cleared' : 'Schedule saved');
      await loadGridData();
    } catch (err: any) {
      console.error('Error assigning schedule:', err);
      handleScheduleApiError(err, 'Failed to assign schedule');
    } finally {
      setLoading(false);
    }
  };

  const assignDialogHasEntry = Boolean(
    assignFormData.userId
    && assignFormData.date
    && assignFormData.campaignId
    && (
      getCellSchedulesForCampaign(assignFormData.userId, assignFormData.date, assignFormData.campaignId).length > 0
      || absenceMap.has(`${assignFormData.userId}_${assignFormData.date}`)
    ),
  );

  const confirmClearSchedule = () => {
    const dateLabel = formatDate(assignFormData.date);
    const message = assignFormData.repeat === 'none' || !assignFormData.repeatEndDate
      ? `Clear the schedule for ${dateLabel}?`
      : `Clear the schedule from ${dateLabel} through ${formatDate(assignFormData.repeatEndDate)}?`;
    return window.confirm(`${message} This cannot be undone.`);
  };

  const getAssignDates = () => (
    assignFormData.scheduleId
      ? [assignFormData.date]
      : buildRepeatDates()
  ).filter((date) => canEditCell(date));

  const confirmScheduleChangeWithAbsences = (dates: string[]) => {
    if (assignFormData.status !== 'scheduled') return true;

    const manualAbsences = dates
      .map((date) => absenceMap.get(`${assignFormData.userId}_${date}`))
      .filter((absence): absence is NonNullable<typeof absence> => Boolean(absence))
      .filter((absence) => !isAutoNoShowAbsence(absence));

    if (manualAbsences.length === 0) return true;

    const uniqueLabels = Array.from(new Set(manualAbsences.map(getAbsenceLabel)));
    const dateLabel = dates.length === 1
      ? formatDate(dates[0])
      : `${formatDate(dates[0])} through ${formatDate(dates[dates.length - 1])}`;

    return window.confirm(
      `This employee has an absence record (${uniqueLabels.join(', ')}) on ${dateLabel}. `
      + 'Changing the schedule will remove the absence. Continue?',
    );
  };

  const handleClearSchedule = async () => {
    if (!assignFormData.campaignId || !canEditCell(assignFormData.date)) return;
    if (!confirmClearSchedule()) return;
    try {
      setLoading(true);
      const dates = assignFormData.scheduleId ? [assignFormData.date] : buildRepeatDates();
      const targetCampaignId = assignFormData.campaignId;
      await Promise.all(dates.map(async (date) => {
        if (!canEditCell(date)) return;
        const cellSchedules = getCellSchedulesForCampaign(assignFormData.userId, date, assignFormData.campaignId);
        const existingAbsence = absenceMap.get(`${assignFormData.userId}_${date}`);
        if (assignFormData.scheduleId) {
          await apiClient.schedules.delete(assignFormData.scheduleId);
        } else {
          await Promise.all(cellSchedules.filter((s) => s.id).map((s) => apiClient.schedules.delete(s.id!)));
        }
        if (existingAbsence) {
          await apiClient.absences.delete(existingAbsence.id);
        }
      }));
      setAssignDialogOpen(false);
      setSuccess('Schedule cleared');
      await loadGridData();
    } catch (err: any) {
      console.error('Error clearing schedule:', err);
      setError(err?.response?.data?.message || 'Failed to clear schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmployee = async () => {
    if (!campaignInfo || !singleCampaignId) return;
    const selectedList = addEmployeeFormData.selectedEmployees;
    if (selectedList.length === 0) return;

    const gridEntries: GridEmployee[] = selectedList.map((selected) => ({
      ...selected,
      gridCampaignId: singleCampaignId,
      gridCampaignName: campaignInfo.name || '',
    }));

    try {
      setLoading(true);
      if (addEmployeeFormData.permanent) {
        await Promise.all(
          selectedList.map((employee) =>
            apiClient.users.update(employee.id, { departmentId: campaignInfo.departmentId }),
          ),
        );
      }

      const names = selectedList.map((employee) => `${employee.firstName} ${employee.lastName}`).join(', ');
      setSuccess(
        selectedList.length === 1
          ? `${names} added — assign shifts using the Not Scheduled cells`
          : `${selectedList.length} employees added (${names}) — assign shifts using the Not Scheduled cells`,
      );

      flushSync(() => {
        setTemporaryEmployees((prev) => {
          const existingIds = new Set(prev.map((emp) => emp.id));
          const toAdd = gridEntries.filter((emp) => !existingIds.has(emp.id));
          return [...prev, ...toAdd];
        });
        setAddEmployeeDialogOpen(false);
        setAddEmployeeFormData({ selectedEmployees: [], permanent: false });
      });
      await loadGridData();
    } catch (err: any) {
      console.error('Error adding employee:', err);
      setError(err?.response?.data?.message || 'Failed to add employee');
    } finally {
      setLoading(false);
    }
  };

  const closeAddEmployeeDialog = () => {
    setAddEmployeeDialogOpen(false);
    setAddEmployeeFormData({ selectedEmployees: [], permanent: false });
  };

  const handlePublishSchedule = async () => {
    if (!singleCampaignId) return;
    try {
      setLoading(true);
      const response = await apiClient.schedules.publishSchedule(singleCampaignId, {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        viewMode
      });
      setSuccess(response.data.message || 'Schedule published');
      const publishResponse = await apiClient.schedules.getPublishStatus(singleCampaignId, dateRange.startDate, dateRange.endDate, viewMode);
      setPublishStatus(publishResponse.data.data || null);
    } catch (err: any) {
      console.error('Error publishing schedule:', err);
      setError(err?.response?.data?.message || 'Failed to publish schedule');
    } finally {
      setLoading(false);
    }
  };

  const statusLegend = (
    <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
      {['scheduled', 'day_off', 'vacation', 'personal', 'holiday', 'no_show', 'sick', 'other', 'missing', 'not_scheduled', 'present', 'unscheduled_work', 'other_campaign_today', 'other_campaign'].map(key => (
        <Chip
          key={key}
          label={STATUS_META[key].label}
          sx={{
            backgroundColor: STATUS_META[key].color,
            color: STATUS_META[key].textColor || (key === 'not_scheduled' ? 'text.primary' : '#fff'),
            border: key === 'not_scheduled' ? '1px solid #e0e0e0' : 'none',
          }}
          size="small"
        />
      ))}
    </Box>
  );

  const handleGridSort = (field: GridSortField) => {
    if (gridSortField === field) {
      setGridSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setGridSortField(field);
    setGridSortDirection('asc');
  };

  const sortGridEmployees = (list: GridEmployee[], statusDate: string) => {
    const direction = gridSortDirection === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      let compare = 0;
      switch (gridSortField) {
        case 'campaign':
          compare = a.gridCampaignName.localeCompare(b.gridCampaignName);
          break;
        case 'employee':
          compare = `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
          break;
        case 'employeeId':
          compare = (a.employeeId || '').localeCompare(b.employeeId || '', undefined, { numeric: true });
          break;
        case 'status':
          compare = getEmployeeCellStatus(a, statusDate).label.localeCompare(
            getEmployeeCellStatus(b, statusDate).label,
          );
          break;
        default:
          break;
      }

      if (compare !== 0) return compare * direction;

      const campaignCompare = a.gridCampaignName.localeCompare(b.gridCampaignName);
      if (campaignCompare !== 0) return campaignCompare;
      return `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`);
    });
  };

  const renderGrid = (dates: string[]) => (
    <TableContainer key={`${dates[0]}-${dates[dates.length - 1]}`}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {isMultiCampaignView && (
              <TableCell sortDirection={gridSortField === 'campaign' ? gridSortDirection : false}>
                <TableSortLabel
                  active={gridSortField === 'campaign'}
                  direction={gridSortField === 'campaign' ? gridSortDirection : 'asc'}
                  onClick={() => handleGridSort('campaign')}
                >
                  Campaign
                </TableSortLabel>
              </TableCell>
            )}
            <TableCell sortDirection={gridSortField === 'employee' ? gridSortDirection : false}>
              <TableSortLabel
                active={gridSortField === 'employee'}
                direction={gridSortField === 'employee' ? gridSortDirection : 'asc'}
                onClick={() => handleGridSort('employee')}
              >
                Employee
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={gridSortField === 'employeeId' ? gridSortDirection : false}>
              <TableSortLabel
                active={gridSortField === 'employeeId'}
                direction={gridSortField === 'employeeId' ? gridSortDirection : 'asc'}
                onClick={() => handleGridSort('employeeId')}
              >
                Employee ID
              </TableSortLabel>
            </TableCell>
            <TableCell sortDirection={gridSortField === 'status' ? gridSortDirection : false}>
              <TableSortLabel
                active={gridSortField === 'status'}
                direction={gridSortField === 'status' ? gridSortDirection : 'asc'}
                onClick={() => handleGridSort('status')}
              >
                Status
              </TableSortLabel>
            </TableCell>
            {dates.map(date => (
              <TableCell key={date} align="center">
                <Box display="flex" flexDirection="column">
                  <Typography variant="caption" fontWeight="bold">
                    {new Date(date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short' })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(date)}
                  </Typography>
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortGridEmployees(filteredEmployees, dates[0]).map(emp => (
            <TableRow key={`${emp.id}_${emp.gridCampaignId}`}>
              {isMultiCampaignView && (
                <TableCell>
                  <Typography variant="caption">{emp.gridCampaignName}</Typography>
                </TableCell>
              )}
              <TableCell>{emp.firstName} {emp.lastName}</TableCell>
              <TableCell>{emp.employeeId}</TableCell>
              <TableCell>
                <Chip label={getEmployeeCellStatus(emp, dates[0]).label} size="small" />
              </TableCell>
              {dates.map(date => {
                const status = getEmployeeCellStatus(emp, date);
                const cellSchedules = getEmployeeCellSchedules(emp, date);
                const otherCampaignSchedules = status.key === 'other_campaign'
                  ? getOtherCampaignSchedules(emp, date)
                  : [];
                const displaySchedules = status.key === 'other_campaign' ? otherCampaignSchedules : cellSchedules;
                const shiftCount = displaySchedules.length;
                const otherCampaignMeta = status.key === 'other_campaign'
                  ? getOtherCampaignStatusMeta(date)
                  : null;
                const color = otherCampaignMeta?.color || STATUS_META[status.key]?.color || '#fff';
                const cellTextColor = otherCampaignMeta?.textColor
                  || STATUS_META[status.key]?.textColor
                  || (status.key === 'not_scheduled' ? 'text.primary' : '#fff');
                const editable = canEditCell(date) && Boolean(emp.gridCampaignId);
                const canOpenOtherCampaignDetails = status.key === 'other_campaign';
                const shiftTooltip = shiftCount > 1
                  ? displaySchedules.map((s) => formatShiftLabel(s, { shiftCount, viewCampaignId: emp.gridCampaignId })).join(' · ')
                  : shiftCount === 1
                    ? formatShiftLabel(displaySchedules[0], { shiftCount, viewCampaignId: emp.gridCampaignId })
                    : status.label;
                const handleCellClick = () => {
                  if (canOpenOtherCampaignDetails) {
                    openOtherCampaignDetails(emp, date);
                    return;
                  }
                  if (editable) {
                    openAssignDialog(emp, date);
                  }
                };
                return (
                  <TableCell
                    key={date}
                    align="center"
                    sx={{
                      backgroundColor: color,
                      color: cellTextColor,
                      border: status.key === 'not_scheduled' ? '1px solid #eee' : 'none',
                      opacity: isPastDate(date) && status.key !== 'other_campaign' ? 0.65 : 1,
                      p: 0.5,
                      minWidth: shiftCount > 1 || status.key === 'other_campaign' ? 88 : undefined,
                    }}
                  >
                    <Tooltip
                      title={
                        canOpenOtherCampaignDetails
                          ? `Scheduled on another campaign — click for details: ${shiftTooltip}`
                          : isPastDate(date)
                          ? 'Past dates cannot be edited'
                          : !emp.gridCampaignId
                            ? 'Campaign is required to edit'
                            : !canEdit
                              ? 'You do not have permission to edit schedules'
                              : shiftCount > 1
                                ? `${shiftCount} shifts: ${shiftTooltip}`
                                : status.label
                      }
                    >
                      <Box
                        onClick={handleCellClick}
                        sx={{
                          cursor: canOpenOtherCampaignDetails || editable
                            ? 'pointer'
                            : isPastDate(date) ? 'not-allowed' : 'default',
                          width: '100%',
                          minHeight: 40,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: status.key === 'not_scheduled' ? 'text.primary' : cellTextColor,
                        }}
                      >
                        <Typography variant="caption" fontWeight="bold">
                          {status.key === 'other_campaign'
                            ? status.label
                            : shiftCount > 1 ? `${shiftCount} shifts` : status.label}
                        </Typography>
                        {status.key === 'other_campaign' ? (
                          <>
                            {displaySchedules.slice(0, 2).map((schedule) => (
                              <Typography
                                key={schedule.id || `${date}-${schedule.campaignId}-${schedule.startTime}`}
                                variant="caption"
                                display="block"
                                sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}
                              >
                                {formatShiftLabel(schedule, { shiftCount, viewCampaignId: emp.gridCampaignId })}
                              </Typography>
                            ))}
                            {shiftCount > 2 && (
                              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                                +{shiftCount - 2} more
                              </Typography>
                            )}
                          </>
                        ) : shiftCount > 1 ? (
                          <>
                            {displaySchedules.slice(0, 2).map((schedule) => (
                              <Typography
                                key={schedule.id}
                                variant="caption"
                                display="block"
                                sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}
                              >
                                {formatShiftLabel(schedule, { shiftCount, viewCampaignId: emp.gridCampaignId })}
                              </Typography>
                            ))}
                            {shiftCount > 2 && (
                              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                                +{shiftCount - 2} more
                              </Typography>
                            )}
                          </>
                        ) : shiftCount === 1 && (
                          <Typography variant="caption" display="block" sx={{ fontSize: '0.65rem' }}>
                            {formatShiftLabel(displaySchedules[0], { shiftCount, viewCampaignId: emp.gridCampaignId })}
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const monthWeeks = useMemo(() => {
    if (viewMode !== 'monthly') return [] as string[][];
    const dates = getMonthDates(selectedMonth);
    const weeks: string[][] = [];
    for (let i = 0; i < dates.length; i += 7) {
      weeks.push(dates.slice(i, i + 7));
    }
    return weeks;
  }, [viewMode, selectedMonth]);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Schedule Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => { setError(null); setScheduleConflict(null); }}>
          <Typography variant="body2">{error}</Typography>
          {scheduleConflict && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
                Conflicting shift: {scheduleConflict.userName} on <strong>{scheduleConflict.campaignName}</strong>
                {' · '}{scheduleConflict.timeLabel} · {scheduleConflict.dateLabel}
              </Typography>
              <Button size="small" variant="outlined" onClick={goToConflictingSchedule}>
                Open {scheduleConflict.campaignName} schedule
              </Button>
            </Box>
          )}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: { xs: 'wrap', md: 'nowrap' },
              mb: 2,
            }}
          >
            <Box sx={{ flex: '1 1 220px', minWidth: 200 }}>
              <Autocomplete
                multiple
                options={campaigns}
                getOptionLabel={(option) => option.name}
                value={selectedCampaigns}
                onChange={(_, value) => setSelectedCampaignIds(value.map((campaign) => campaign.id))}
                disableCloseOnSelect
                ListboxComponent={CampaignListboxWithActions}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox size="small" sx={{ mr: 1 }} checked={selected} />
                    {option.name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Campaign" placeholder={selectedCampaigns.length === 0 ? 'Select campaigns' : ''} />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
              {campaigns.length > 1 && (
                <Button
                  size="small"
                  variant="text"
                  onClick={handleToggleAllCampaigns}
                  sx={{ mt: 0.5, px: 0, minWidth: 0, textTransform: 'none' }}
                >
                  {allCampaignsSelected
                    ? 'Exclude all'
                    : `All (${campaigns.length} campaigns)`}
                </Button>
              )}
            </Box>
            <Box sx={{ flex: '0 0 170px' }}>
              {viewMode === 'weekly' ? (
                <TextField
                  fullWidth
                  label="Week Start"
                  type="date"
                  value={selectedWeek}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value && !/^\d{4}-\d{2}-\d{2}$/.test(value)) return;
                    setSelectedWeek(value);
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              ) : (
                <TextField
                  fullWidth
                  label="Month"
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value && !/^\d{4}-\d{2}$/.test(value)) return;
                    setSelectedMonth(value);
                  }}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            </Box>
            <Box sx={{ flex: '0 0 auto' }}>
              <Tabs value={viewMode} onChange={(e, value) => setViewMode(value)}>
                <Tab value="weekly" label="Weekly" icon={<WeekIcon />} iconPosition="start" />
                <Tab value="monthly" label="Monthly" icon={<MonthIcon />} iconPosition="start" />
              </Tabs>
            </Box>
            <Box sx={{ flex: '0 0 auto' }}>
              <Tooltip
                arrow
                title={timeMode === 'mine'
                  ? `Showing shift times in your time zone: ${zoneLabel(viewerTimezone)}`
                  : `Showing shift times in each employee's own time zone`}
              >
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={timeMode}
                  onChange={(_e, value) => {
                    if (value !== 'mine' && value !== 'agent') return;
                    setTimeMode(value);
                    try { localStorage.setItem('scheduleTimeMode', value); } catch { /* ignore */ }
                  }}
                  aria-label="Schedule time zone display"
                >
                  <ToggleButton value="mine" aria-label="My time">My Time</ToggleButton>
                  <ToggleButton value="agent" aria-label="Agent time">Agent Time</ToggleButton>
                </ToggleButtonGroup>
              </Tooltip>
            </Box>
            <Box sx={{ flex: '0 0 auto', display: 'flex', gap: 1, ml: { md: 'auto' }, flexShrink: 0 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setAddEmployeeDialogOpen(true)}
                disabled={!singleCampaignId || !canEditSchedule}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Add Employee
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<PublishIcon />}
                onClick={handlePublishSchedule}
                disabled={!singleCampaignId || !canEdit || isSchedulePublished}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Publish Schedule
              </Button>
            </Box>
          </Box>
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              {selectedCampaignIds.length === 1 && campaignInfo?.name
                ? `Campaign: ${campaignInfo.name} (${scheduledEmployeeCount} employees with shifts this ${viewMode === 'weekly' ? 'week' : 'month'})`
                : selectedCampaignIds.length > 1
                  ? `${selectedCampaignIds.length} campaigns (${scheduledEmployeeCount} employees with shifts)`
                  : 'Select one or more campaigns'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {isMultiCampaignView
                ? 'Publish and add employee are available when a single campaign is selected.'
                : isSchedulePublished
                  ? `Published ${new Date(publishStatus.publishedAt).toLocaleString()} — future dates can still be edited`
                  : 'Not published'}
            </Typography>
            {statusLegend}
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as typeof STATUS_OPTIONS[number])}
                  label="Status"
                >
                  {STATUS_OPTIONS.map(option => (
                    <MenuItem key={option} value={option}>
                      {option === 'all' ? 'All' : STATUS_META[option].label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by employee name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={5} display="flex" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={openCopyDialog}
                disabled={selectedCampaignIds.length === 0 || !canEdit || viewMode !== 'weekly' || copySourceEmployees.length === 0}
              >
                Copy Schedule
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {selectedCampaignIds.length === 0 ? (
        <Alert severity="info">Select one or more campaigns to load the schedule.</Alert>
      ) : viewMode === 'weekly' ? (
        renderGrid(dateRange.dates)
      ) : (
        <Box display="flex" flexDirection="column" gap={3}>
          {monthWeeks.map((week, index) => (
            <Card key={`${week[0]}-${index}`}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Week of {formatDate(week[0])}
                </Typography>
                {renderGrid(week)}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Copy Schedule Dialog */}
      <Dialog open={copyDialogOpen} onClose={() => !copying && setCopyDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Copy Weekly Schedule</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Copy schedules from {formatDate(sourceWeekStart)}–{formatDate(sourceWeekEnd)} for{' '}
            <strong>{copyCampaignLabel}</strong>. The first copy covers{' '}
            <strong>{formatDate(firstCopyWeekStart)}–{formatDate(minCopyEndDate)}</strong> (through{' '}
            {formatDate(minCopyEndDate)} inclusive). Extend the end date to copy additional weeks.
          </Typography>
          <TextField
            fullWidth
            label="Copy Until"
            type="date"
            value={copyEndDate}
            onChange={(e) => setCopyEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: minCopyEndDate }}
            helperText={
              copyWeekCount > 0
                ? `${copyWeekCount} week${copyWeekCount === 1 ? '' : 's'} will be copied through ${formatDate(copyEndDate)} inclusive. Existing schedules are skipped.`
                : `Default is ${formatDate(minCopyEndDate)} (1 week). Pick a later date to copy more weeks.`
            }
            sx={{ mb: 3 }}
          />
          <Typography variant="subtitle2" gutterBottom>
            Employees to copy ({selectedCopyEmployees.length} of {copySourceEmployees.length} selected)
          </Typography>
          {copySourceEmployees.length === 0 ? (
            <Alert severity="info">No employees with schedules in this week for the selected campaigns.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allCopyEmployeesSelected}
                        indeterminate={someCopyEmployeesSelected && !allCopyEmployeesSelected}
                        onChange={(e) => toggleAllCopyEmployees(e.target.checked)}
                      />
                    </TableCell>
                    <TableCell sortDirection={copySortField === 'campaign' ? copySortDirection : false}>
                      <TableSortLabel
                        active={copySortField === 'campaign'}
                        direction={copySortField === 'campaign' ? copySortDirection : 'asc'}
                        onClick={() => handleCopySort('campaign')}
                      >
                        Campaign
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={copySortField === 'employee' ? copySortDirection : false}>
                      <TableSortLabel
                        active={copySortField === 'employee'}
                        direction={copySortField === 'employee' ? copySortDirection : 'asc'}
                        onClick={() => handleCopySort('employee')}
                      >
                        Employee
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={copySortField === 'employeeId' ? copySortDirection : false}>
                      <TableSortLabel
                        active={copySortField === 'employeeId'}
                        direction={copySortField === 'employeeId' ? copySortDirection : 'asc'}
                        onClick={() => handleCopySort('employeeId')}
                      >
                        Employee ID
                      </TableSortLabel>
                    </TableCell>
                    {dateRange.dates.map((date) => (
                      <TableCell
                        key={date}
                        align="center"
                        sortDirection={copySortField === `day:${date}` ? copySortDirection : false}
                      >
                        <TableSortLabel
                          active={copySortField === `day:${date}`}
                          direction={copySortField === `day:${date}` ? copySortDirection : 'asc'}
                          onClick={() => handleCopySort(`day:${date}`)}
                        >
                          <Box display="flex" flexDirection="column" alignItems="center">
                            <Typography variant="caption" fontWeight="bold" lineHeight={1.2}>
                              {new Date(date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short' })}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
                              {formatDate(date)}
                            </Typography>
                          </Box>
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell sortDirection={copySortField === 'scheduledHours' ? copySortDirection : false}>
                      <TableSortLabel
                        active={copySortField === 'scheduledHours'}
                        direction={copySortField === 'scheduledHours' ? copySortDirection : 'asc'}
                        onClick={() => handleCopySort('scheduledHours')}
                      >
                        Scheduled Hrs
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedCopySourceEmployees.map((emp) => {
                    const employeeKey = getCopyEmployeeKey(emp);
                    const isSelected = copySelectedEmployeeKeys.has(employeeKey);
                    return (
                      <TableRow
                        key={employeeKey}
                        hover
                        selected={isSelected}
                        onClick={() => toggleCopyEmployee(employeeKey)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={isSelected}
                            onChange={() => toggleCopyEmployee(employeeKey)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{emp.gridCampaignName}</Typography>
                        </TableCell>
                        <TableCell>{emp.firstName} {emp.lastName}</TableCell>
                        <TableCell>{emp.employeeId}</TableCell>
                        {dateRange.dates.map((date) => {
                          const dayShifts = getCellSchedulesForCampaign(emp.id, date, emp.gridCampaignId);
                          return (
                            <TableCell key={date} align="center">
                              {dayShifts.length === 0 ? (
                                <Typography variant="caption" color="text.disabled">—</Typography>
                              ) : (
                                <Stack spacing={0.25}>
                                  {dayShifts.map((shift) => (
                                    <Typography key={shift.id || `${date}-${shift.startTime}`} variant="caption" color="text.secondary">
                                      {displayShiftRange(shift)}
                                    </Typography>
                                  ))}
                                </Stack>
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell align="right">
                          {roundHours(
                            getEmployeeWeekScheduledHours(emp, dateRange.dates, getCellSchedulesForCampaign),
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyDialogOpen(false)} disabled={copying}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<CopyIcon />}
            onClick={handleCopySchedule}
            disabled={copying || selectedCopyEmployees.length === 0 || copyWeekCount === 0}
          >
            {copying ? 'Copying...' : 'Copy Schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Day Shifts Dialog (multiple shifts on same day, or other-campaign details) */}
      <Dialog open={dayShiftsDialog.open} onClose={closeDayShiftsDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {dayShiftsDialog.otherCampaignOnly
            ? `Scheduled on another campaign — ${dayShiftsDialog.employeeName}`
            : `Shifts — ${dayShiftsDialog.employeeName}`}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {formatDate(dayShiftsDialog.date)}
            {dayShiftsDialog.otherCampaignOnly && (
              <> · Viewing from {campaigns.find((c) => c.id === dayShiftsDialog.campaignId)?.name || 'this campaign'}</>
            )}
          </Typography>
          {dayShiftsDialog.otherCampaignOnly && (
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              This agent is not scheduled on the filtered campaign for this day, but has shift(s) on another campaign.
            </Typography>
          )}
          <List dense disablePadding>
            {getCellSchedules(dayShiftsDialog.userId, dayShiftsDialog.date)
              .filter((schedule) => (
                dayShiftsDialog.otherCampaignOnly
                  ? Boolean(schedule.campaignId) && schedule.campaignId !== dayShiftsDialog.campaignId
                  : !schedule.campaignId || schedule.campaignId === dayShiftsDialog.campaignId
              ))
              .map((schedule, index, dayShifts) => (
              <React.Fragment key={schedule.id || `${schedule.campaignId}-${schedule.startTime}-${index}`}>
                {index > 0 && <Divider component="li" />}
                <ListItem>
                  <ListItemText
                    primary={formatShiftLabel(schedule, { shiftCount: dayShifts.length, viewCampaignId: dayShiftsDialog.campaignId })}
                    secondary={
                      schedule.campaignId && schedule.campaignId !== dayShiftsDialog.campaignId
                        ? getScheduleCampaignName(schedule)
                        : undefined
                    }
                  />
                  {!dayShiftsDialog.otherCampaignOnly && (
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        aria-label="Edit shift"
                        disabled={loading || !canEditCell(dayShiftsDialog.date)}
                        onClick={() => {
                          const emp = mergedEmployees.find(
                            (e) => e.id === dayShiftsDialog.userId && e.gridCampaignId === dayShiftsDialog.campaignId,
                          );
                          if (!emp) return;
                          closeDayShiftsDialog();
                          openAssignDialogForSchedule(emp, dayShiftsDialog.date, schedule);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        edge="end"
                        size="small"
                        aria-label="Delete shift"
                        disabled={loading || !canEditCell(dayShiftsDialog.date)}
                        onClick={() => schedule.id && handleDeleteShift(schedule.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          {!dayShiftsDialog.otherCampaignOnly && (
            <>
              <Button
                startIcon={<AddIcon />}
                onClick={() => {
                  const emp = mergedEmployees.find(
                    (e) => e.id === dayShiftsDialog.userId && e.gridCampaignId === dayShiftsDialog.campaignId,
                  );
                  if (!emp) return;
                  closeDayShiftsDialog();
                  openAssignDialogForSchedule(emp, dayShiftsDialog.date, null, { isNewShift: true });
                }}
                disabled={loading || !canEditCell(dayShiftsDialog.date)}
              >
                Add shift
              </Button>
              <Button
                color="error"
                onClick={handleClearAllShiftsForDay}
                disabled={loading || !canEditCell(dayShiftsDialog.date)}
              >
                Clear all
              </Button>
            </>
          )}
          {dayShiftsDialog.otherCampaignOnly && canEditCell(dayShiftsDialog.date) && (
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                const emp = mergedEmployees.find(
                  (e) => e.id === dayShiftsDialog.userId && e.gridCampaignId === dayShiftsDialog.campaignId,
                );
                if (!emp) return;
                closeDayShiftsDialog();
                openAssignDialogForSchedule(emp, dayShiftsDialog.date, null, { isNewShift: true });
              }}
              disabled={loading}
            >
              Assign on this campaign
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={closeDayShiftsDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Shift Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {assignFormData.status === 'not_scheduled'
            ? 'Clear Schedule'
            : assignFormData.scheduleId
              ? 'Edit Shift'
              : getCellSchedulesForCampaign(assignFormData.userId, assignFormData.date, assignFormData.campaignId).length > 0
                ? 'Add Shift'
                : 'Assign Shift'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {assignFormData.scheduleId
              && getCellSchedulesForCampaign(assignFormData.userId, assignFormData.date, assignFormData.campaignId).length > 1 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Editing one shift on this day. Other shifts are unchanged.
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={assignFormData.status}
                    onChange={(e) => setAssignFormData({ ...assignFormData, status: e.target.value as AssignFormData['status'] })}
                    label="Status"
                  >
                    <MenuItem value="scheduled">Scheduled</MenuItem>
                    <MenuItem value="not_scheduled">Not Scheduled (clear)</MenuItem>
                    <MenuItem value="day_off">Day Off</MenuItem>
                    <MenuItem value="holiday">Holiday</MenuItem>
                    {canAssignVacation && (
                      <MenuItem value="vacation">Vacation</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              {assignFormData.status === 'not_scheduled' && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    Removes the shift and any day-off/holiday for this date, returning the cell to Not Scheduled.
                  </Alert>
                </Grid>
              )}
              {assignFormData.status !== 'not_scheduled' && (
                <>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Campaign</InputLabel>
                  <Select
                    value={assignFormData.campaignId}
                    onChange={(e) => setAssignFormData({ ...assignFormData, campaignId: e.target.value })}
                    label="Campaign"
                  >
                    {campaigns.map((campaign) => (
                      <MenuItem key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Time"
                  type="time"
                  value={assignFormData.startTime}
                  onChange={(e) => setAssignFormData({ ...assignFormData, startTime: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  helperText={editorTimeHelper}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Time"
                  type="time"
                  value={assignFormData.endTime}
                  onChange={(e) => setAssignFormData({ ...assignFormData, endTime: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  helperText={editorTimeHelper}
                />
              </Grid>
                </>
              )}
              {(assignFormData.status === 'day_off' || assignFormData.status === 'holiday' || assignFormData.status === 'vacation') && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Reason (optional)"
                    value={assignFormData.reason}
                    onChange={(e) => setAssignFormData({ ...assignFormData, reason: e.target.value })}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <FormControl fullWidth disabled={Boolean(assignFormData.scheduleId)}>
                  <InputLabel>Repeat</InputLabel>
                  <Select
                    value={assignFormData.repeat}
                    onChange={(e) => setAssignFormData({ ...assignFormData, repeat: e.target.value as AssignFormData['repeat'] })}
                    label="Repeat"
                  >
                    <MenuItem value="none">Do Not Repeat</MenuItem>
                    <MenuItem value="daily">Repeat Daily</MenuItem>
                    <MenuItem value="weekly">Repeat Weekly</MenuItem>
                    <MenuItem value="biweekly">Repeat Biweekly</MenuItem>
                    <MenuItem value="monthly">Repeat Monthly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {assignFormData.repeat !== 'none' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Repeat End Date"
                    type="date"
                    value={assignFormData.repeatEndDate}
                    onChange={(e) => setAssignFormData({ ...assignFormData, repeatEndDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          {assignDialogHasEntry && assignFormData.status !== 'not_scheduled' && (
            <Button color="error" onClick={handleClearSchedule} disabled={loading}>
              {assignFormData.scheduleId ? 'Delete shift' : 'Clear schedule'}
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAssignShift}
            variant="contained"
            color={assignFormData.status === 'not_scheduled' ? 'error' : 'primary'}
            disabled={loading}
          >
            {loading
              ? 'Saving...'
              : assignFormData.status === 'not_scheduled'
                ? 'Clear'
                : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Employee Dialog */}
      <Dialog open={addEmployeeDialogOpen} onClose={closeAddEmployeeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Employee</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={addableEmployees}
                  value={addEmployeeFormData.selectedEmployees}
                  onChange={(_, value) => setAddEmployeeFormData({
                    ...addEmployeeFormData,
                    selectedEmployees: value,
                  })}
                  getOptionLabel={getEmployeeOptionLabel}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  disableCloseOnSelect
                  filterSelectedOptions
                  limitTags={3}
                  noOptionsText="No matching employees"
                  renderOption={(props, option, { selected }) => (
                    <li {...props} key={option.id}>
                      <Checkbox size="small" sx={{ mr: 1 }} checked={selected} />
                      {getEmployeeOptionLabel(option)}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Employees"
                      placeholder={addEmployeeFormData.selectedEmployees.length === 0 ? 'Search by name or employee ID' : ''}
                    />
                  )}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  {addEmployeeFormData.selectedEmployees.length === 0
                    ? 'Type to search, then select one or more employees.'
                    : `${addEmployeeFormData.selectedEmployees.length} selected`}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Assign Permanently</InputLabel>
                  <Select
                    value={addEmployeeFormData.permanent ? 'yes' : 'no'}
                    onChange={(e) => setAddEmployeeFormData({ ...addEmployeeFormData, permanent: e.target.value === 'yes' })}
                    label="Assign Permanently"
                  >
                    <MenuItem value="no">Temporary (this schedule only)</MenuItem>
                    <MenuItem value="yes">Permanent assignment to campaign</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddEmployeeDialog}>Cancel</Button>
          <Button
            onClick={handleAddEmployee}
            variant="contained"
            disabled={addEmployeeFormData.selectedEmployees.length === 0 || loading}
          >
            {loading
              ? 'Saving...'
              : addEmployeeFormData.selectedEmployees.length > 1
                ? `Add ${addEmployeeFormData.selectedEmployees.length} Employees`
                : 'Add Employee'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ScheduleManagementPage;
